// Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { ethers } from 'ethers'

import { CompanyProfileCTO } from './CompanyProfileCTO'

import { RoleEnum, StageName, WalletEnum } from './Enums'
import { EthAddr } from './SchemaTypes'
import { ChainAccountValues } from './LiquidityAsset'

// PROVIDERS
export const LOCAL_HH_URL = 'http://127.0.0.1:8545'
export const SEPOLIA_JEST_URL = 'https://eth-sepolia.g.alchemy.com/v2/0yaiy8KJZuYR87vj9PqNTX2BtjXQiYMe'

export const PLUME_TESTNET_URL = 'https://testnet-rpc.plumenetwork.xyz'
export const PLUME_MAINNET_URL = 'https://phoenix-rpc.plumenetwork.xyz'

export const DEV_SEPOLIA_FRONTEND_URL = 'https://eth-sepolia.g.alchemy.com/v2/eiPlOW8JsDv6XX2Nx7pNM'
export const DEV_SEPOLIA_BACKEND_URL = 'https://eth-sepolia.g.alchemy.com/v2/T7jthncxhDOfM0ZNo05Hd'
export const DEV_FUJI_FRONTEND_URL = 'https://avax-fuji.g.alchemy.com/v2/8PA73IMpIFVh6JJWT73vE'
export const DEV_FUJI_BACKEND_URL = 'https://avax-fuji.g.alchemy.com/v2/TY9d7HjOrUmauijGrBvF3'
export const DEV_PLUME_TESTNET_FRONTEND_URL = PLUME_TESTNET_URL
export const DEV_PLUME_TESTNET_BACKEND_URL = PLUME_TESTNET_URL

export const QA_SEPOLIA_FRONTEND_URL = 'https://eth-sepolia.g.alchemy.com/v2/ZfYMU4EmOzmizBu2ybqI8'
export const QA_SEPOLIA_BACKEND_URL = 'https://eth-sepolia.g.alchemy.com/v2/0Sa6UL3L0k0HbKdr-h5SJ'
export const QA_FUJI_FRONTEND_URL = 'https://avax-fuji.g.alchemy.com/v2/4cSE9_-9aE5rv6QJtMm9U'
export const QA_FUJI_BACKEND_URL = 'https://avax-fuji.g.alchemy.com/v2/_ESugHwjHIW_-ezv1xUvy'
export const QA_PLUME_TESTNET_FRONTEND_URL = PLUME_TESTNET_URL
export const QA_PLUME_TESTNET_BACKEND_URL = PLUME_TESTNET_URL

export const SANDBOX_SEPOLIA_FRONTEND_URL = 'https://eth-sepolia.g.alchemy.com/v2/C2preuKi6gctek2xm0eYF'
export const SANDBOX_SEPOLIA_BACKEND_URL = 'https://eth-sepolia.g.alchemy.com/v2/ZusYanyOFeHQBHqjCrw3R'
export const SANDBOX_FUJI_FRONTEND_URL = 'https://avax-fuji.g.alchemy.com/v2/rVoz9ehjmnDc5Etx5YEry'
export const SANDBOX_FUJI_BACKEND_URL = 'https://avax-fuji.g.alchemy.com/v2/jxIKtD4v8GHBDV-sI-eYV'
export const SANDBOX_PLUME_MAINNET_FRONTEND_URL = PLUME_MAINNET_URL
export const SANDBOX_PLUME_MAINNET_BACKEND_URL = PLUME_MAINNET_URL

export const PROD_MAINNET_FRONTEND_URL = 'https://eth-mainnet.g.alchemy.com/v2/q9zjx0r2FcVd2jickusbV'
export const PROD_MAINNET_BACKEND_URL = 'https://eth-mainnet.g.alchemy.com/v2/QJqYiOEGbhFQtwKjJUs03'
export const PROD_AVALANCHE_FRONTEND_URL = 'https://avax-mainnet.g.alchemy.com/v2/p5dgOkAqxryvxryB4To24'
export const PROD_AVALANCHE_BACKEND_URL = 'https://avax-mainnet.g.alchemy.com/v2/YZGQb5WG7Ax8YmC51A7vW'
export const PROD_PLUME_MAINNET_FRONTEND_URL = PLUME_MAINNET_URL
export const PROD_PLUME_MAINNET_BACKEND_URL = PLUME_MAINNET_URL

export const MAINNET_URL = 'https://eth-mainnet.g.alchemy.com/v2/fcK4AEYquRkIgqn0REy8N0uOsiO3kqKI'
export const MAINNET_INFURA_URL = 'https://mainnet.infura.io/v3/a2b8f2b01fae4ce2bbb96dc3baf9360d'

//export const FUJI_URL = 'https://avalanche-fuji.infura.io/v3/a2b8f2b01fae4ce2bbb96dc3baf9360d'
export const FUJI_URL = 'https://avax-fuji.g.alchemy.com/v2/0WCXrt7anW-WEu8UtmZeHdcjTvvSuW8W'
export const AVALANCHE_URL = 'https://avax-mainnet.g.alchemy.com/v2/-GxeTmEVs_DmUMWGQXxnjiJ6rLVPa1V_'

// REVIEW: Use a dedicated one
export const CHAIN_ID_HARDHAT = 31337
export const CHAIN_ID_GOERLI = 5
export const CHAIN_ID_SEPOLIA = 11155111
export const CHAIN_ID_MAINNET = 1
export const CHAIN_ID_FUJI = 43113
export const CHAIN_ID_AVALANCHE = 43114
export const CHAIN_ID_PLUME_TESTNET = 98867
export const CHAIN_ID_PLUME_MAINNET = 98866

// Protocol address
export const DEV_SEPOLIA_FOR_JEST_SERVICE_CONFIGURATION_ADDR = '0x549C830B222Ac346ec27c603a5Da46d345989816'
export const AVALANCHE_SERVICE_CONFIGURATION_ADDR = '0xa3bE07c2734ed1CE269Cf026Cbe8D09d146014e1'
export const LOCALHOST_UPGRADE_SERVICE_CONFIGURATION_ADDR = '0xDDb38137De84439b0045e19CCA8fB1880002474b'
export const LOCALHOST_SERVICE_CONFIGURATION_ADDR = '0x549C830B222Ac346ec27c603a5Da46d345989816' // '0x29AD3b3231a7a1a2f5Bc44f2C3dA27F50Cb1cB97'
export const MAINNET_SERVICE_CONFIGURATION_ADDR = '0xec4F65e0A0268ff38ECEc711AD5159b96dAbaB98'
export const QA2_SERVICE_CONFIGURATION_ADDR = '0x9e653B96b90eE3F60db0084271693f35cf572BAD' // '0x29AD3b3231a7a1a2f5Bc44f2C3dA27F50Cb1cB97'
export const QASEP_SERVICE_CONFIGURATION_ADDR = '0xC14b54FA8417D2a1D6Ecc31563e0a5e9E090C449' // '0xbedd20F7613b4cCEbdDf7C7296460319B8e1934E' // '0xfF8C215A77847cd152AD095d0bEDFa2f70617c74' // '0x4B9AD4442F220199FE4a61093126A1E95Ce29D61' // '0x9455e81215d0176347632C93C78bA060B2FF03FB' //'0x1D6e4758EE51Ed1976700fc1efd594cCff9436F9' // '0x3d5bfB2D620F8eF3077f2df5033FdbDB8872969F'
export const TOM_SERVICE_CONFIGURATION_ADDR = '0x62aF6aa1511FE897ffB6950268004538aa9A1494' //'0x3d5bfB2D620F8eF3077f2df5033FdbDB8872969F' // '0xC022987cb57110A4110519a749416af39ffF170E'
export const DEV_SEPOLIA_SERVICE_CONFIGURATION_ADDR = '0x2D2a27C3a778414DaD4Db79589e83725184aC64B' // '0x8EF4F274B38D1e2Bb5edAD1Fab46D0Fb596967B4' // '0x5737804c6F0681E64f3CAFbE645ee00353b82B83'

export const SANDBOX_SERVICE_CONFIGURATION_ADDR = '0xf08676003E05175cdD48fbD90b2AC3de230b4CDB' // '0x526eCD1166cD535316627e73BBf092a959a2156F'
export const QA_FUJI_SERVICE_CONFIGURATION_ADDR = '0x7660F78F8dD9Be7Dc6F1B14c5E79Ed4833a88939' // '0x7760Ac87014De4014512Cc1af35e614eF6a46117' // '0x755449CBAa22E162B9Be9048085E1Ec3702B2Da8'
export const DEV_FUJI_SERVICE_CONFIGURATION_ADDR = '0xbEe469ECA2aC896154A224826521aE74b24D370B' // '0x106D52b73046373b3019b90842750Fc63B2758a8' // '0x755449CBAa22E162B9Be9048085E1Ec3702B2Da8'
export const SANDBOX_FUJI_SERVICE_CONFIGURATION_ADDR = '0xF061a3e9fAf00eA3FB8b3316AdDd3eB9792FE13b'

export const DEV_PLUME_TESTNET_SERVICE_CONFIGURATION_ADDR = '0x314869E236c2BFb7827c3b882C7473F541f535F2'
export const QA_PLUME_TESTNET_SERVICE_CONFIGURATION_ADDR = '0xFe9a0e57d8046d1Ebb6179FF9C48BCc7FA5D1439'
export const SANDBOX_PLUME_MAINNET_SERVICE_CONFIGURATION_ADDR = '0x52F5535997C71B40e3b7A59B69F51120Afc86159'
export const PROD_PLUME_MAINNET_SERVICE_CONFIGURATION_ADDR = '0x785B01F10496Ec28AAcEc6fb19Aec8A191199a18'

export const SEPOLIA_USDC_ADDR = '0x540B3AE6Cd8bC6248Fe705cd379b6c7613099062'
export const SEPOLIA_EURC_ADDR = '0xdD8c995DC283949ACc687DC577Fd9E64D5caa7Dd'

export const FUJI_USDC_ADDR = '0xBc0Aa760964BD838f462610fC996b24Ff09ca0B9'
export const FUJI_EURC_ADDR = '0x51Ab0ee26D7652Cd943Ca362167CB570C27AA6Ac'

// BLOCK_EXPLORERS
// TODO: #859 Refactor usage of etherscan_url to rely on blockExplorerUrl and aux functions like getTransactionBlockExplorerUrl and getAddressBlockExplorerUrl
export const ETHERSCAN_FUJI = 'https://testnet.snowtrace.io/tx'
export const ETHERSCAN_MAINNET = 'https://etherscan.io/tx'
export const ETHERSCAN_GOERLI = 'https://goerli.etherscan.io/tx'
export const ETHERSCAN_SEPOLIA = 'https://sepolia.etherscan.io/tx'
export const ETHERSCAN_AVALANCHE = 'https://snowtrace.io/tx'
export const ETHERSCAN_PLUME_TESTNET = 'https://testnet-explorer.plume.org/tx'
export const ETHERSCAN_PLUME_MAINNET = 'https://phoenix-explorer.plumenetwork.xyz/tx'
export const WALLET_CONNECT_PROJECT_ID = '95a7421b836a16c2c5b981ab3393a4ca'
export const WALLET_CONNECT_DEV_PROJECT_ID = '9c66a40540005698d2ccabbb3a734523'

export const BLOCK_EXPLORER_FUJI = 'https://testnet.snowtrace.io/'
export const BLOCK_EXPLORER_MAINNET = 'https://etherscan.io/'
export const BLOCK_EXPLORER_SEPOLIA = 'https://sepolia.etherscan.io/'
export const BLOCK_EXPLORER_AVALANCHE = 'https://snowtrace.io/'
export const BLOCK_EXPLORER_PLUME_TESTNET = 'https://testnet-explorer.plume.org/'
export const BLOCK_EXPLORER_PLUME_MAINNET = 'https://phoenix-explorer.plumenetwork.xyz/'

// REST_API_KEYS
export const DEV_REST_API_KEY = 'AeVkJSjdPy9IlXBnXsH3857FQTcHgTuR4xqL7bE4'
export const QA_REST_API_KEY = 'YT89KuGtNc7Q21cO8v0Zr6MD7EPEbiE99KKDvt7j'
export const SANDBOX_REST_API_KEY = 'WE8etBkeeT7a4rhN8G3778KPSScG7Pms7KvFbpq0'
export const PROD_REST_API_KEY = 'bljIxYzvER2ky16J5mY3p64uWn1qdkCQ6zfpCBX4'
export const PROD_CANARY_REST_API_KEY = '8WwMT4vgPE63JmRhEAI7m4w93z48sQTJ9jdU3oSq'
export const SANDBOX_CANARY_REST_API_KEY = 'cdyZFw7sCa7ejzNLS56q46jHzoNeTnT97VY9nR1j'
export const LOCAL_REST_API_KEY = 'TODO'

let REACT_APP_ACCOUNT_OPERATOR: string | undefined = undefined
let REACT_APP_ACCOUNT_POOL_ADMIN: string | undefined = undefined
let REACT_APP_ACCOUNT_LENDER1: string | undefined = undefined
let REACT_APP_ACCOUNT_BORROWER: string | undefined = undefined
let REACT_APP_ACCOUNT_LENDER2: string | undefined = undefined
let REACT_APP_OPERATOR: string | undefined = undefined
let REACT_APP_POOL_ADMIN: string | undefined = undefined
let REACT_APP_BORROWER: string | undefined = undefined
let REACT_APP_LENDER1: string | undefined = undefined
let REACT_APP_LENDER2: string | undefined = undefined

try {
  REACT_APP_ACCOUNT_OPERATOR = process.env.REACT_APP_ACCOUNT_OPERATOR
  REACT_APP_ACCOUNT_POOL_ADMIN = process.env.REACT_APP_ACCOUNT_POOL_ADMIN
  REACT_APP_ACCOUNT_BORROWER = process.env.REACT_APP_ACCOUNT_BORROWER
  REACT_APP_ACCOUNT_LENDER1 = process.env.REACT_APP_ACCOUNT_LENDER1
  REACT_APP_ACCOUNT_LENDER2 = process.env.REACT_APP_ACCOUNT_LENDER2 as string
  REACT_APP_POOL_ADMIN = process.env.REACT_APP_POOL_ADMIN as string
  REACT_APP_BORROWER = process.env.REACT_APP_BORROWER as string
  REACT_APP_LENDER1 = process.env.REACT_APP_LENDER1 as string
  REACT_APP_LENDER2 = process.env.REACT_APP_LENDER2 as string
  REACT_APP_OPERATOR = process.env.REACT_APP_OPERATOR as string
} catch {
  REACT_APP_ACCOUNT_OPERATOR = undefined
  REACT_APP_ACCOUNT_POOL_ADMIN = undefined
  REACT_APP_ACCOUNT_LENDER1 = undefined
  REACT_APP_ACCOUNT_BORROWER = undefined
  REACT_APP_ACCOUNT_LENDER2 = undefined
  REACT_APP_POOL_ADMIN = undefined
  REACT_APP_BORROWER = undefined
  REACT_APP_LENDER1 = undefined
  REACT_APP_LENDER2 = undefined
  REACT_APP_OPERATOR = undefined
  // noop
}

export const LOCAL_HOST = process.env.GITHUB_RUN_NUMBER ? 'localdev' : 'localdev'

export const IGNORED_POOLS: EthAddr[] = ['0x3b532b1eA13F6d0D2dFB837c22b72eAdF1294538'] // Flex Pool avalanche

export interface V4Configuration {
  serverApiBaseUrlV4: string
  websocketUrlV4: string
  graphqlUrlV4: string
  fileUploadFolderNamV4: string
  stageName: string

  restAPIKeyV4: string
}
export type ChainConfigName =
  | 'Hardhat'
  | 'DevFuji'
  | 'DevSepolia'
  | 'QaFuji'
  | 'QaSepolia'
  | 'SandboxFuji'
  | 'SandboxSepolia'
  | 'Ethereum'
  | 'Avalanche'
  | 'DevPlume'
  | 'QaPlume'
  | 'SandboxPlume'
  | 'Plume'

export type ChainIconName =
  | 'ethereum'
  | 'avalanche'
  | 'hardhat'
  | 'sepolia'
  | 'fuji'
  | 'base'
  | 'baseSepolia'
  | 'plume'
  | 'plumeTestnet'
  | 'unconnected'

export interface ChainConfiguration {
  chainConfigurationName: ChainConfigName
  chainId: number
  chainName: string
  etherscanUrl?: string
  blockExplorerUrl?: string
  serviceConfigurationAddr: EthAddr
  frontendProviderUrl: string
  backendProviderUrl: string
  backupProviderUrl?: string
  walletConnectProjectId?: string
  chainIcon: ChainIconName
  v4Config: V4Configuration

  // Network specific details
  nativeCurrency: {
    decimals: number
    name: string
    symbol: string
  }
  contracts?: any
  isTestnet: boolean
}

export interface UserConnection {
  roleEnum?: RoleEnum
  walletEnum: WalletEnum
  signerKey?: string
  accountAddr?: string
  companyProfile?: CompanyProfileCTO
  signer?: ethers.Signer

  accountBalances?: ChainAccountValues
  signedMessage?: string
}

export interface CognitoConfig {
  clientId: string
  userPoolId: string
  region: string
}

export interface StageConfiguration {
  stageName: StageName // from serverless.yml
  domain: string
  serverApiBaseUrl: string
  restAPIKey: string
  websocketUrl: string
  graphqlUrl: string
  chainConfigurations: ChainConfiguration[]
  userConnections: UserConnection[]
  cognitoConfig?: CognitoConfig
}

const V4CONFIG_LOCALHOST: V4Configuration = {
  serverApiBaseUrlV4: `http://${LOCAL_HOST}:7000`,
  websocketUrlV4: `http://${LOCAL_HOST}:3001`,
  graphqlUrlV4: `http://${LOCAL_HOST}:7000`,
  fileUploadFolderNamV4: 'localDev',
  stageName: 'localhost',
  restAPIKeyV4: LOCAL_REST_API_KEY,
}
const CHAIN_CONFIG_LOCALHOST: ChainConfiguration = {
  chainConfigurationName: 'Hardhat',
  chainId: CHAIN_ID_SEPOLIA,
  chainName: 'LocalHardhat',
  etherscanUrl: undefined,
  blockExplorerUrl: BLOCK_EXPLORER_SEPOLIA,
  serviceConfigurationAddr: DEV_SEPOLIA_FOR_JEST_SERVICE_CONFIGURATION_ADDR,
  frontendProviderUrl: LOCAL_HH_URL,
  backendProviderUrl: LOCAL_HH_URL,
  walletConnectProjectId: undefined,
  chainIcon: 'hardhat',

  v4Config: V4CONFIG_LOCALHOST,
  nativeCurrency: {
    decimals: 18,
    name: 'Ether',
    symbol: 'ETH',
  },
  contracts: {
    multicall3: {
      address: `0xca11bde05977b3631167028862be2a173976ca11` as `0x${string}`,
      blockCreated: 751532,
    },
  },
  isTestnet: true,
}

const CHAIN_CONFIG_DEV_FUJI: ChainConfiguration = {
  chainConfigurationName: 'DevFuji',
  chainId: CHAIN_ID_FUJI,
  chainName: 'Fuji',
  blockExplorerUrl: BLOCK_EXPLORER_FUJI,
  etherscanUrl: ETHERSCAN_FUJI,
  serviceConfigurationAddr: DEV_FUJI_SERVICE_CONFIGURATION_ADDR,
  frontendProviderUrl: DEV_FUJI_FRONTEND_URL,
  backendProviderUrl: DEV_FUJI_BACKEND_URL,
  walletConnectProjectId: WALLET_CONNECT_DEV_PROJECT_ID,
  chainIcon: 'fuji',
  v4Config: V4CONFIG_LOCALHOST,
  nativeCurrency: {
    decimals: 18,
    name: 'Avalanche Fuji',
    symbol: 'AVAX',
  },
  contracts: {
    multicall3: {
      address: `0xca11bde05977b3631167028862be2a173976ca11` as `0x${string}`,
      blockCreated: 7096959,
    },
  },
  isTestnet: true,
}

const CHAIN_CONFIG_QA_FUJI: ChainConfiguration = {
  chainConfigurationName: 'QaFuji',
  chainId: CHAIN_ID_FUJI,
  chainName: 'Fuji',
  blockExplorerUrl: BLOCK_EXPLORER_FUJI,
  etherscanUrl: ETHERSCAN_FUJI,
  serviceConfigurationAddr: QA_FUJI_SERVICE_CONFIGURATION_ADDR,
  frontendProviderUrl: QA_FUJI_FRONTEND_URL,
  backendProviderUrl: QA_FUJI_BACKEND_URL,
  walletConnectProjectId: WALLET_CONNECT_PROJECT_ID,
  chainIcon: 'fuji',
  v4Config: V4CONFIG_LOCALHOST,
  nativeCurrency: {
    decimals: 18,
    name: 'Avalanche Fuji',
    symbol: 'AVAX',
  },
  contracts: {
    multicall3: {
      address: `0xca11bde05977b3631167028862be2a173976ca11` as `0x${string}`,
      blockCreated: 7096959,
    },
  },
  isTestnet: true,
}

const CHAIN_CONFIG_SANDBOX_FUJI: ChainConfiguration = {
  chainConfigurationName: 'SandboxFuji',
  chainId: CHAIN_ID_FUJI,
  chainName: 'Fuji',
  blockExplorerUrl: BLOCK_EXPLORER_FUJI,
  etherscanUrl: ETHERSCAN_FUJI,
  serviceConfigurationAddr: SANDBOX_FUJI_SERVICE_CONFIGURATION_ADDR,
  frontendProviderUrl: SANDBOX_FUJI_FRONTEND_URL,
  backendProviderUrl: SANDBOX_FUJI_BACKEND_URL,
  walletConnectProjectId: WALLET_CONNECT_PROJECT_ID,
  chainIcon: 'fuji',
  v4Config: V4CONFIG_LOCALHOST,
  nativeCurrency: {
    decimals: 18,
    name: 'Avalanche Fuji',
    symbol: 'AVAX',
  },
  contracts: {
    multicall3: {
      address: `0xca11bde05977b3631167028862be2a173976ca11` as `0x${string}`,
      blockCreated: 7096959,
    },
  },
  isTestnet: true,
}

const CHAIN_CONFIG_DEV_PLUME_TESTNET: ChainConfiguration = {
  chainConfigurationName: 'DevPlume',
  chainId: CHAIN_ID_PLUME_TESTNET,
  chainName: 'Plume',
  blockExplorerUrl: BLOCK_EXPLORER_PLUME_TESTNET,
  etherscanUrl: ETHERSCAN_PLUME_TESTNET,
  serviceConfigurationAddr: DEV_PLUME_TESTNET_SERVICE_CONFIGURATION_ADDR,
  frontendProviderUrl: DEV_PLUME_TESTNET_FRONTEND_URL,
  backendProviderUrl: DEV_PLUME_TESTNET_BACKEND_URL,
  walletConnectProjectId: WALLET_CONNECT_DEV_PROJECT_ID,
  chainIcon: 'plumeTestnet',
  v4Config: V4CONFIG_LOCALHOST,
  nativeCurrency: {
    decimals: 18,
    name: 'Plume',
    symbol: 'PLUME',
  },
  contracts: {
    multicall3: {
      address: `0xca11bde05977b3631167028862be2a173976ca11` as `0x${string}`,
      blockCreated: 7096959,
    },
  },
  isTestnet: true,
}

const CHAIN_CONFIG_QA_PLUME_TESTNET: ChainConfiguration = {
  chainConfigurationName: 'QaPlume',
  chainId: CHAIN_ID_PLUME_TESTNET,
  chainName: 'Plume',
  blockExplorerUrl: BLOCK_EXPLORER_PLUME_TESTNET,
  etherscanUrl: ETHERSCAN_PLUME_TESTNET,
  serviceConfigurationAddr: QA_PLUME_TESTNET_SERVICE_CONFIGURATION_ADDR,
  frontendProviderUrl: QA_PLUME_TESTNET_FRONTEND_URL,
  backendProviderUrl: QA_PLUME_TESTNET_BACKEND_URL,
  walletConnectProjectId: WALLET_CONNECT_PROJECT_ID,
  chainIcon: 'plumeTestnet',
  v4Config: V4CONFIG_LOCALHOST,
  nativeCurrency: {
    decimals: 18,
    name: 'Plume',
    symbol: 'PLUME',
  },
  contracts: {
    multicall3: {
      address: `0xca11bde05977b3631167028862be2a173976ca11` as `0x${string}`,
      blockCreated: 7096959,
    },
  },
  isTestnet: true,
}

const CHAIN_CONFIG_SANDBOX_PLUME_MAINNET: ChainConfiguration = {
  chainConfigurationName: 'SandboxPlume',
  chainId: CHAIN_ID_PLUME_MAINNET,
  chainName: 'Plume',
  blockExplorerUrl: BLOCK_EXPLORER_PLUME_MAINNET,
  etherscanUrl: ETHERSCAN_PLUME_MAINNET,
  serviceConfigurationAddr: SANDBOX_PLUME_MAINNET_SERVICE_CONFIGURATION_ADDR,
  frontendProviderUrl: SANDBOX_PLUME_MAINNET_FRONTEND_URL,
  backendProviderUrl: SANDBOX_PLUME_MAINNET_BACKEND_URL,
  walletConnectProjectId: WALLET_CONNECT_PROJECT_ID,
  chainIcon: 'plume',
  v4Config: V4CONFIG_LOCALHOST,
  nativeCurrency: {
    decimals: 18,
    name: 'Plume',
    symbol: 'PLUME',
  },
  contracts: {
    multicall3: {
      address: `0xca11bde05977b3631167028862be2a173976ca11` as `0x${string}`,
      blockCreated: 7096959,
    },
  },
  isTestnet: false,
}

const CHAIN_CONFIG_PROD_PLUME: ChainConfiguration = {
  chainConfigurationName: 'Plume',
  chainId: CHAIN_ID_PLUME_MAINNET,
  chainName: 'Plume',
  blockExplorerUrl: BLOCK_EXPLORER_PLUME_MAINNET,
  etherscanUrl: ETHERSCAN_PLUME_MAINNET,
  serviceConfigurationAddr: PROD_PLUME_MAINNET_SERVICE_CONFIGURATION_ADDR,
  frontendProviderUrl: PROD_PLUME_MAINNET_FRONTEND_URL,
  backendProviderUrl: PROD_PLUME_MAINNET_BACKEND_URL,
  walletConnectProjectId: WALLET_CONNECT_PROJECT_ID,
  chainIcon: 'plume',
  v4Config: V4CONFIG_LOCALHOST,
  nativeCurrency: {
    decimals: 18,
    name: 'Plume',
    symbol: 'PLUME',
  },
  contracts: {
    multicall3: {
      address: `0xca11bde05977b3631167028862be2a173976ca11` as `0x${string}`,
      blockCreated: 7096959,
    },
  },
  isTestnet: false,
}

const CHAIN_CONFIG_DEV_SEPOLIA: ChainConfiguration = {
  chainConfigurationName: 'DevSepolia',
  chainId: CHAIN_ID_SEPOLIA,
  chainName: 'Sepolia',
  blockExplorerUrl: BLOCK_EXPLORER_SEPOLIA,
  etherscanUrl: ETHERSCAN_SEPOLIA,
  serviceConfigurationAddr: DEV_SEPOLIA_SERVICE_CONFIGURATION_ADDR,
  frontendProviderUrl: DEV_SEPOLIA_FRONTEND_URL,
  backendProviderUrl: DEV_SEPOLIA_BACKEND_URL,
  walletConnectProjectId: WALLET_CONNECT_DEV_PROJECT_ID,
  chainIcon: 'sepolia',
  v4Config: V4CONFIG_LOCALHOST,
  nativeCurrency: {
    decimals: 18,
    name: 'Sepolia Ether',
    symbol: 'ETH',
  },
  contracts: {
    multicall3: {
      address: `0xca11bde05977b3631167028862be2a173976ca11` as `0x${string}`,
      blockCreated: 751532,
    },
    ensRegistry: { address: `0x00000000000C2E074eC69A0dFb2997BA6C7d2e1e` as `0x${string}` },
    ensUniversalResolver: {
      address: `0xc8Af999e38273D658BE1b921b88A9Ddf005769cC` as `0x${string}`,
      blockCreated: 5_317_080,
    },
  },
  isTestnet: true,
}

const CHAIN_CONFIG_QA_SEPOLIA: ChainConfiguration = {
  chainConfigurationName: 'QaSepolia',
  chainId: CHAIN_ID_SEPOLIA,
  chainName: 'Sepolia',
  blockExplorerUrl: BLOCK_EXPLORER_SEPOLIA,
  etherscanUrl: ETHERSCAN_SEPOLIA,
  serviceConfigurationAddr: QASEP_SERVICE_CONFIGURATION_ADDR,
  frontendProviderUrl: QA_SEPOLIA_FRONTEND_URL,
  backendProviderUrl: QA_SEPOLIA_BACKEND_URL,
  walletConnectProjectId: WALLET_CONNECT_PROJECT_ID,
  chainIcon: 'sepolia',
  v4Config: V4CONFIG_LOCALHOST,
  nativeCurrency: {
    decimals: 18,
    name: 'Sepolia Ether',
    symbol: 'ETH',
  },
  contracts: {
    multicall3: {
      address: `0xca11bde05977b3631167028862be2a173976ca11` as `0x${string}`,
      blockCreated: 751532,
    },
    ensRegistry: { address: `0x00000000000C2E074eC69A0dFb2997BA6C7d2e1e` as `0x${string}` },
    ensUniversalResolver: {
      address: `0xc8Af999e38273D658BE1b921b88A9Ddf005769cC` as `0x${string}`,
      blockCreated: 5_317_080,
    },
  },
  isTestnet: true,
}

const CHAIN_CONFIG_PROD_MAINNET: ChainConfiguration = {
  chainConfigurationName: 'Ethereum',
  chainId: CHAIN_ID_MAINNET,
  chainName: 'mainnet',
  blockExplorerUrl: BLOCK_EXPLORER_MAINNET,
  etherscanUrl: ETHERSCAN_MAINNET,
  serviceConfigurationAddr: MAINNET_SERVICE_CONFIGURATION_ADDR,
  frontendProviderUrl: PROD_MAINNET_FRONTEND_URL,
  backendProviderUrl: PROD_MAINNET_BACKEND_URL,
  walletConnectProjectId: WALLET_CONNECT_PROJECT_ID,
  chainIcon: 'ethereum',
  v4Config: V4CONFIG_LOCALHOST,
  nativeCurrency: {
    decimals: 18,
    name: 'Ether',
    symbol: 'ETH',
  },
  contracts: {
    multicall3: {
      address: `0xca11bde05977b3631167028862be2a173976ca11` as `0x${string}`,
      blockCreated: 14353601,
    },
  },
  isTestnet: false,
}

const CHAIN_CONFIG_PROD_AVALANCHE: ChainConfiguration = {
  chainConfigurationName: 'Avalanche',
  chainId: CHAIN_ID_AVALANCHE,
  chainName: 'Avalanche',
  blockExplorerUrl: BLOCK_EXPLORER_AVALANCHE,
  etherscanUrl: ETHERSCAN_AVALANCHE,
  serviceConfigurationAddr: AVALANCHE_SERVICE_CONFIGURATION_ADDR,
  frontendProviderUrl: PROD_AVALANCHE_FRONTEND_URL,
  backendProviderUrl: PROD_AVALANCHE_BACKEND_URL,
  walletConnectProjectId: WALLET_CONNECT_PROJECT_ID,
  chainIcon: 'avalanche',
  v4Config: V4CONFIG_LOCALHOST,
  nativeCurrency: {
    decimals: 18,
    name: 'Avalanche',
    symbol: 'AVAX',
  },
  contracts: {
    multicall3: {
      address: `0xca11bde05977b3631167028862be2a173976ca11` as `0x${string}`,
      blockCreated: 11907934,
    },
  },
  isTestnet: false,
}

const CHAIN_CONFIG_SANDBOX_SEPOLIA: ChainConfiguration = {
  chainConfigurationName: 'SandboxSepolia',
  chainId: CHAIN_ID_SEPOLIA,
  chainName: 'Sepolia',
  blockExplorerUrl: BLOCK_EXPLORER_SEPOLIA,
  etherscanUrl: ETHERSCAN_SEPOLIA,
  serviceConfigurationAddr: SANDBOX_SERVICE_CONFIGURATION_ADDR,
  frontendProviderUrl: SANDBOX_SEPOLIA_FRONTEND_URL,
  backendProviderUrl: SANDBOX_SEPOLIA_BACKEND_URL,
  walletConnectProjectId: WALLET_CONNECT_PROJECT_ID,
  chainIcon: 'sepolia',
  v4Config: V4CONFIG_LOCALHOST,
  nativeCurrency: {
    decimals: 18,
    name: 'Sepolia Ether',
    symbol: 'ETH',
  },
  contracts: {
    multicall3: {
      address: `0xca11bde05977b3631167028862be2a173976ca11` as `0x${string}`,
      blockCreated: 751532,
    },
    ensRegistry: { address: `0x00000000000C2E074eC69A0dFb2997BA6C7d2e1e` as `0x${string}` },
    ensUniversalResolver: {
      address: `0xc8Af999e38273D658BE1b921b88A9Ddf005769cC` as `0x${string}`,
      blockCreated: 5_317_080,
    },
  },
  isTestnet: true,
}

const USER_CONNECTION_OPERATOR: UserConnection = {
  roleEnum: RoleEnum.OPERATOR,
  walletEnum: WalletEnum.PRIVATE_KEY_STRING,
  signerKey: REACT_APP_ACCOUNT_OPERATOR as string,
  accountAddr: REACT_APP_OPERATOR as string,
  companyProfile: {
    uuid: '',
    roleEnum: RoleEnum.POOL_ADMIN,
    companyName: 'OpenTrade test Operator',
    wallets: [REACT_APP_POOL_ADMIN as string],
    emails: ['tom+testOperator@open-trade.io'],
  },
  signer: undefined,
}

const USER_CONNECTION_POOLADMIN: UserConnection = {
  roleEnum: RoleEnum.POOL_ADMIN,
  walletEnum: WalletEnum.PRIVATE_KEY_STRING,
  signerKey: REACT_APP_ACCOUNT_POOL_ADMIN as string,
  accountAddr: REACT_APP_POOL_ADMIN as string,
  companyProfile: {
    uuid: '',
    roleEnum: RoleEnum.POOL_ADMIN,
    companyName: 'OpenTrade test Pool Admin',
    wallets: [REACT_APP_POOL_ADMIN as string],
    emails: ['tom+testPooladmin@open-trade.io'],
  },
  signer: undefined,
}

const USER_CONNECTION_BORROWER: UserConnection = {
  roleEnum: RoleEnum.BORROWER,
  walletEnum: WalletEnum.PRIVATE_KEY_STRING,
  signerKey: REACT_APP_ACCOUNT_BORROWER as string,
  accountAddr: REACT_APP_BORROWER as string,
  companyProfile: {
    uuid: '',
    roleEnum: RoleEnum.BORROWER,
    companyName: 'OpenTrade test Borrower',
    wallets: [REACT_APP_BORROWER as string],
    emails: ['tom+testBorrower@open-trade.io'],
  },
  signer: undefined,
}

const USER_CONNECTION_LENDER1: UserConnection = {
  roleEnum: RoleEnum.LENDER,
  walletEnum: WalletEnum.PRIVATE_KEY_STRING,
  signerKey: REACT_APP_ACCOUNT_LENDER1 as string,
  accountAddr: REACT_APP_LENDER1 as string,
  companyProfile: {
    uuid: '',
    roleEnum: RoleEnum.LENDER,
    companyName: 'OpenTrade test Lender1',
    wallets: [REACT_APP_LENDER1 as string],
    emails: ['tom+testLender1@open-trade.io'],
  },
  signer: undefined,
}

const USER_CONNECTION_LENDER2: UserConnection = {
  roleEnum: RoleEnum.LENDER,
  walletEnum: WalletEnum.PRIVATE_KEY_STRING,
  signerKey: REACT_APP_ACCOUNT_LENDER2 as string,
  accountAddr: REACT_APP_LENDER2 as string,
  companyProfile: {
    uuid: '',
    roleEnum: RoleEnum.LENDER,
    companyName: 'OpenTrade test Lender2',
    wallets: [REACT_APP_LENDER2 as string],
    emails: ['tom+testLender2@open-trade.io'],
  },
  signer: undefined,
}

const LOCAL_STAGE_CONFIG: StageConfiguration = {
  stageName: 'local', // from serverless.yml
  domain: 'localhost',
  serverApiBaseUrl: `http://${LOCAL_HOST}:7000`,
  websocketUrl: `http://${LOCAL_HOST}:3001`,
  graphqlUrl: `http://${LOCAL_HOST}:7000`,
  restAPIKey: 'TODO',
  chainConfigurations: [CHAIN_CONFIG_LOCALHOST],
  userConnections: [
    USER_CONNECTION_POOLADMIN,
    USER_CONNECTION_BORROWER,
    USER_CONNECTION_LENDER1,
    USER_CONNECTION_LENDER2,
    USER_CONNECTION_OPERATOR,
  ],
}

const DEVOTTO_STAGE_CONFIG: StageConfiguration = {
  stageName: 'devotto', // from serverless.yml
  domain: 'app.opentradedevotto.com',
  websocketUrl: 'wss://ws.opentradedevotto.com',
  serverApiBaseUrl: 'https://api.opentradedevotto.com',
  graphqlUrl: 'https://gql.opentradedevotto.com',
  restAPIKey: DEV_REST_API_KEY,
  chainConfigurations: [CHAIN_CONFIG_DEV_SEPOLIA, CHAIN_CONFIG_DEV_FUJI, CHAIN_CONFIG_DEV_PLUME_TESTNET],
  userConnections: [],
  cognitoConfig: {
    clientId: '1i417bmld3u3s369mtc0qukpiu',
    userPoolId: 'us-east-1_uM41RLrBr',
    region: 'us-east-1',
  },
}

const DEVTOM_STAGE_CONFIG: StageConfiguration = {
  stageName: 'devtom', // from serverless.yml
  domain: 'app.opentradedevtom.com',
  websocketUrl: 'wss://ws.opentradedevtom.com',
  serverApiBaseUrl: 'https://api.opentradedevtom.com',
  graphqlUrl: 'https://gql.opentradedevtom.com',
  restAPIKey: DEV_REST_API_KEY,
  chainConfigurations: [CHAIN_CONFIG_DEV_SEPOLIA, CHAIN_CONFIG_DEV_FUJI, CHAIN_CONFIG_DEV_PLUME_TESTNET],
  userConnections: [],
  cognitoConfig: {
    clientId: '47739008flkps0ghfksfsj70s2',
    userPoolId: 'us-east-1_uM41RLrBr',
    region: 'us-east-1',
  },
}

const DEV_STAGE_CONFIG: StageConfiguration = {
  stageName: 'dev', // from serverless.yml
  domain: 'app.opentradedev.com',
  websocketUrl: 'wss://ws.opentradedev.com',
  serverApiBaseUrl: 'https://api.opentradedev.com',
  graphqlUrl: 'https://gql.opentradedev.com',
  restAPIKey: DEV_REST_API_KEY,
  chainConfigurations: [CHAIN_CONFIG_DEV_SEPOLIA, CHAIN_CONFIG_DEV_FUJI, CHAIN_CONFIG_DEV_PLUME_TESTNET],
  userConnections: [],
  cognitoConfig: {
    clientId: '1rpajhqvmct8h9t1g2tagc6ibo',
    userPoolId: 'us-east-1_uM41RLrBr',
    region: 'us-east-1',
  },
}

const QA_STAGE_CONFIG: StageConfiguration = {
  stageName: 'qa', // from serverless.yml
  domain: 'app.opentradeqa.com',
  websocketUrl: 'wss://ws.opentradeqa.com',
  serverApiBaseUrl: 'https://api.opentradeqa.com',
  graphqlUrl: 'https://gql.opentradeqa.com',
  restAPIKey: QA_REST_API_KEY,
  chainConfigurations: [CHAIN_CONFIG_QA_SEPOLIA, CHAIN_CONFIG_QA_FUJI, CHAIN_CONFIG_QA_PLUME_TESTNET],
  userConnections: [],
  cognitoConfig: {
    clientId: 'lap2ehse3165qke0vq66n5nd7',
    userPoolId: 'us-east-1_pradU8JuA',
    region: 'us-east-1',
  },
}

const SANDBOX_STAGE_CONFIG: StageConfiguration = {
  stageName: 'sandbox', // from serverless.yml
  domain: 'app.opentradesandbox.com',
  websocketUrl: 'wss://ws.opentradesandbox.com',
  serverApiBaseUrl: 'https://api.opentradesandbox.com',
  graphqlUrl: 'https://gql.opentradesandbox.com',
  restAPIKey: SANDBOX_REST_API_KEY,
  chainConfigurations: [CHAIN_CONFIG_SANDBOX_SEPOLIA, CHAIN_CONFIG_SANDBOX_FUJI],
  userConnections: [],
  cognitoConfig: {
    clientId: '15l61romc29tet2oua4tpv3f50',
    userPoolId: 'us-east-1_GahGaxth9',
    region: 'us-east-1',
  },
}

const SANDBOX_CANARY_STAGE_CONFIG: StageConfiguration = {
  stageName: 'sandboxcanary', // from serverless.yml
  domain: 'app.opentradesandboxcanary.com',
  websocketUrl: 'wss://ws.opentradesandboxcanary.com',
  serverApiBaseUrl: 'https://api.opentradesandboxcanary.com',
  graphqlUrl: 'https://gql.opentradesandboxcanary.com',
  restAPIKey: SANDBOX_CANARY_REST_API_KEY,
  chainConfigurations: [CHAIN_CONFIG_SANDBOX_SEPOLIA, CHAIN_CONFIG_SANDBOX_FUJI, CHAIN_CONFIG_SANDBOX_PLUME_MAINNET],
  userConnections: [],
  cognitoConfig: {
    clientId: '323knfuk2aq9i89ekhms7jvjl9',
    userPoolId: 'us-east-1_GahGaxth9',
    region: 'us-east-1',
  },
}

const PROD_STAGE_CONFIG: StageConfiguration = {
  stageName: 'prod', // from serverless.yml
  domain: 'app.open-trade.io',
  websocketUrl: 'wss://ws.open-trade.io',
  serverApiBaseUrl: 'https://api.open-trade.io',
  graphqlUrl: 'https://gql.open-trade.io',
  restAPIKey: PROD_REST_API_KEY,
  chainConfigurations: [CHAIN_CONFIG_PROD_MAINNET, CHAIN_CONFIG_PROD_AVALANCHE, CHAIN_CONFIG_PROD_PLUME],
  userConnections: [],
  cognitoConfig: {
    clientId: '6gbjr0f6uvnnvqv19spql3slb6',
    userPoolId: 'us-east-1_DsQFQxybT',
    region: 'us-east-1',
  },
}

const PROD_CANARY_STAGE_CONFIG: StageConfiguration = {
  stageName: 'prodcanary', // from serverless.yml
  domain: 'app.opentradecanary.com',
  websocketUrl: 'wss://ws.opentradecanary.com',
  serverApiBaseUrl: 'https://api.opentradecanary.com',
  graphqlUrl: 'https://gql.opentradecanary.com',
  restAPIKey: PROD_CANARY_REST_API_KEY,
  chainConfigurations: [CHAIN_CONFIG_PROD_MAINNET, CHAIN_CONFIG_PROD_AVALANCHE, CHAIN_CONFIG_PROD_PLUME],
  userConnections: [],
  cognitoConfig: {
    clientId: '4dp7gp0ijg829lfldt849ifovr',
    userPoolId: 'us-east-1_nL1ysOltK',
    region: 'us-east-1',
  },
}

export const STAGE_CONFIGURATIONS: StageConfiguration[] = [
  LOCAL_STAGE_CONFIG,
  DEV_STAGE_CONFIG,
  DEVOTTO_STAGE_CONFIG,
  DEVTOM_STAGE_CONFIG,
  QA_STAGE_CONFIG,
  SANDBOX_STAGE_CONFIG,
  SANDBOX_CANARY_STAGE_CONFIG,
  PROD_STAGE_CONFIG,
  PROD_CANARY_STAGE_CONFIG,
]

export const BLOCK_EXPLORER_API_CONFIG: Record<string, { url: string; apiKey: string }> = {
  mainnet: {
    url: 'https://api.etherscan.io/api',
    apiKey: process.env.ETHERSCAN_API_KEY_MAINNET || 'AMHP1YPT8IFGNH3AGDMM8XIQPRTW4TFV2A',
  },
  sepolia: {
    url: 'https://api-sepolia.etherscan.io/api',
    apiKey: process.env.ETHERSCAN_API_KEY_SEPOLIA || 'AMHP1YPT8IFGNH3AGDMM8XIQPRTW4TFV2A',
  },
  fuji: {
    url: 'https://api.routescan.io/v2/network/testnet/evm/43113/etherscan/api',
    apiKey: process.env.ETHERSCAN_API_KEY_FUJI || '',
  },
  avalanche: {
    url: 'https://api.routescan.io/v2/network/mainnet/evm/43114/etherscan/api',
    apiKey: process.env.ETHERSCAN_API_KEY_AVALANCHE || '',
  },
}

export const findStageConfiguration = (stageName: StageName): StageConfiguration => {
  const stageConfig = STAGE_CONFIGURATIONS.find((dc) => {
    if (dc.stageName.toLowerCase() === stageName.toLowerCase()) {
      return true
    }

    return false
  })

  if (stageConfig) {
    return stageConfig
  }

  throw new Error('findStageConfiguration: unknown stageName: ' + stageName)
}

export const findChainConfiguration = (chainConfigurationName: ChainConfigName): ChainConfiguration | undefined => {
  const stageConfig = findStageConfiguration(process.env.stage as StageName)
  const chainConfig = stageConfig.chainConfigurations.find((cc) => cc.chainConfigurationName === chainConfigurationName)
  return chainConfig
}

export const convertToAppKitNetwork = (chainConfig: ChainConfiguration): any => {
  const getBlockExplorerName = (chainName: string) => {
    switch (chainName) {
      case 'Avalanche':
      case 'Fuji':
        return 'SnowTrace'
      case 'Plume':
        return 'Plume Explorer'
      default:
        return 'Etherscan'
    }
  }

  return {
    id: chainConfig.chainId,
    name: chainConfig.chainName,
    nativeCurrency: chainConfig.nativeCurrency,
    rpcUrls: {
      default: { http: [chainConfig.frontendProviderUrl] },
    },
    blockExplorers: {
      default: {
        name: getBlockExplorerName(chainConfig.chainName),
        url: chainConfig.blockExplorerUrl || '',
      },
    },
    contracts: chainConfig.contracts,
    testnet: chainConfig.isTestnet,
  }
}
